import React, { useState, useEffect } from 'react';
import { FormRenderer } from '../../../content-builder/FormRenderer';
import Badge from '../../../Badge';
import Button from '../../../Button';
import { clubDocumentApi } from '../../../../services/clubDocumentApi';

/**
 * Step 3: Preview & Test Form
 *
 * Purpose: Preview template and fill test data for PDF generation
 *
 * Features:
 * - Display template metadata
 * - Interactive form renderer for test data entry
 * - Generate test PDF button
 */
const Step3_PreviewAndTest = ({
  wizardData,
  onDataChange,
  onValidationChange,
  onGenerateTestPdf
}) => {
  const [testFormData, setTestFormData] = useState({});
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // This step is always valid - user can skip PDF generation
    onValidationChange(true);
  }, [onValidationChange]);

  const handleTestFormDataChange = (data) => {
    setTestFormData(data);
    // Store test form data in wizard data for potential use
    onDataChange({
      ...wizardData,
      testFormData: data
    });
  };

  const handleGenerateTestPdf = async () => {
    try {
      setGeneratingPdf(true);
      setError(null);

      // Call backend API to generate test PDF
      const response = await clubDocumentApi.generateTestPdf({
        template_title: wizardData.title,
        template_content: wizardData.content,
        template_version: '1.0',
        form_data: testFormData,
        signature_data: testFormData.signature
      });

      // Create blob URL for PDF preview
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Store PDF URL in wizard data
      onDataChange({
        ...wizardData,
        testPdfUrl: url,
        testFormData: testFormData
      });

      // Notify parent to advance to next step
      if (onGenerateTestPdf) {
        onGenerateTestPdf(url);
      }

    } catch (err) {
      console.error('Error generating test PDF:', err);
      setError('Failed to generate test PDF. Please check the form content and try again.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const getCategoryLabel = (category) => {
    const categories = {
      waiver: 'Waiver',
      medical: 'Medical',
      registration: 'Registration',
      emergency_contact: 'Emergency Contact',
      concussion: 'Concussion',
      insurance: 'Insurance',
      other: 'Other'
    };
    return categories[category] || category;
  };

  const getAcceptanceModeLabel = (mode) => {
    const modes = {
      signature: 'Signature Required',
      acknowledgment: 'Acknowledgment Only',
      accept: 'Accept/Acknowledge'
    };
    return modes[mode] || mode;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Preview & Test Form</h3>
        <p className="text-sm text-gray-600 mb-6">
          Review your template and fill out test data for PDF generation
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Template Preview Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-semibold text-gray-900">{wizardData.title}</h3>
          <div className="flex gap-2">
            <Badge variant="info">{getCategoryLabel(wizardData.category)}</Badge>
            <Badge variant={wizardData.acceptance_mode === 'accept' ? 'primary' : 'secondary'}>
              {getAcceptanceModeLabel(wizardData.acceptance_mode)}
            </Badge>
            {wizardData.active && <Badge variant="success">Active</Badge>}
          </div>
        </div>

        {wizardData.description && (
          <div className="text-gray-600 mb-4">
            <p>{wizardData.description}</p>
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
        <p className="text-sm">
          Fill in the form fields below with test data. Click "Generate Test PDF" to see how the final PDF will look.
          You can also skip this step and proceed directly to create the template.
        </p>
      </div>

      {/* Form Renderer for test data entry */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-sm font-semibold text-gray-500 mb-4">Test Form</h4>
        <FormRenderer
          content={wizardData.content}
          onFormDataChange={handleTestFormDataChange}
          showSignature={wizardData.acceptance_mode === 'accept'}
          acceptanceMode={wizardData.acceptance_mode}
          documentTitle={wizardData.title}
          readOnly={false}
        />
      </div>

      {/* Generate Test PDF Button */}
      <div className="flex justify-center pt-4">
        <Button
          onClick={handleGenerateTestPdf}
          disabled={generatingPdf}
          size="lg"
          className="min-w-48"
        >
          {generatingPdf ? 'Generating PDF...' : '📄 Generate Test PDF'}
        </Button>
      </div>

      {/* Skip Note */}
      <div className="text-center text-sm text-gray-500">
        <p>You can skip PDF generation and proceed to the next step using the "Next" button above.</p>
      </div>
    </div>
  );
};

export { Step3_PreviewAndTest };
